/*
 *  -----------------------------------------------------------------------
 *    <copyright file="range-selector.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import React, { useState, useEffect } from 'react';
import DraggableList from './draggable-list';
import EditRange from './edit-range';
import { IPanelGraphCategory } from './panelGraphCategory.interface';
import { useBoolean, useTranslation } from 'hooks';
import './styles.scss';
import Constants from 'utils/constants';

const RangeSelector = ({ data, onChangeCategory }: { data: IPanelGraphCategory[]; onChangeCategory: Function }) => {
    const { t } = useTranslation();
    const openEditRange = useBoolean(false);
    const [items, setItems] = useState([]);
    const [currentCategory, setCurrentCategory] = useState(null);

    useEffect(() => {
        setItems(data.map((item: IPanelGraphCategory, index: number) => formatItem(item, index)));
    }, [data]);

    const checkForNullAndUndefined = (value: any) => value === undefined || value === null;

    const formatItem = (item: IPanelGraphCategory, index: number) => {
        const newItem = { ...item };
        const fullClause = Constants.clauses.find((el: any) => el.id === item.clause);
        newItem.key =
            !checkForNullAndUndefined(item.min) && !checkForNullAndUndefined(item.max) && item.clause === 'NONE'
                ? `${item.min}${item.max}`
                : `${item.min}${item.clause}`;
        newItem.title =
            !checkForNullAndUndefined(item.min) && !checkForNullAndUndefined(item.max) && item.clause === 'NONE'
                ? `${item.min} - ${item.max}`
                : `${fullClause?.text} ${item.min}`;
        newItem.index = index;
        return newItem;
    };

    return (
        <>
            <EditRange
                open={openEditRange.value}
                onClose={(newItem: IPanelGraphCategory) => {
                    if (newItem) {
                        const array = [...items];
                        if (currentCategory) array[newItem.index] = { ...formatItem(newItem, newItem.index) };
                        else array.push(formatItem(newItem, array.length));
                        setItems(array);
                        onChangeCategory([...array]);
                    }
                    setCurrentCategory(null);
                    openEditRange.setFalse();
                }}
                currentCategory={currentCategory}
                categories={items}
            />
            <div className="categories-wrapper">
                <div className="header">
                    <p className="category-title">{t('cm:categories')}</p>
                    <div className="add-range-btn" id="add-range-btn" onClick={openEditRange.setTrue}>
                        +
                    </div>
                </div>
                <div className="content">
                    {items?.length ? (
                        <DraggableList
                            data={items}
                            onChangeCategory={(data: IPanelGraphCategory[]) => {
                                const reindexedArray = data.map((item: IPanelGraphCategory, index: number) => ({
                                    ...item,
                                    index,
                                }));
                                setItems([...reindexedArray]);
                                onChangeCategory([...reindexedArray]);
                            }}
                            onEditCategory={(item: IPanelGraphCategory, index: number) => {
                                setCurrentCategory(item);
                                openEditRange.setTrue();
                            }}
                        />
                    ) : (
                        <p className="no-categories-title">{t('cm:noCategoriesYet')}</p>
                    )}
                </div>
            </div>
        </>
    );
};

export default RangeSelector;
